import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandLockup, BrandMark } from '../components/Brand';
import { ErrorMessage, Field } from '../components/ui';
import { IconMoon, IconRoute, IconShield, IconSun, IconTasks, IconTarget } from '../components/icons';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';

export function LoginPage() {
  const { login } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await login(email, password, totp || undefined);
      if (result.twoFactorRequired) {
        setNeedsTotp(true);
      } else {
        navigate('/');
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-aside">
        <div className="row" style={{ gap: 14 }}>
          <BrandMark size={58} />
          <div>
            <strong style={{ fontSize: 20, letterSpacing: '-0.03em' }}>FacilityFlow</strong>
            <div style={{ fontSize: 12, opacity: 0.82 }}>Taskmanagement für Immobilienbetreuung</div>
          </div>
        </div>

        <h2>Vom Anruf bis zur Abrechnung – ein durchgängiger Prozess.</h2>

        <ul>
          <li>
            <span className="feature-icon">
              <IconTasks width={17} height={17} />
            </span>
            <div>
              <strong>Kanalunabhängige Erfassung</strong>
              Web, Mobil, E-Mail, Telefon, QR-Code am Objekt oder Schnittstelle.
            </div>
          </li>
          <li>
            <span className="feature-icon">
              <IconTarget width={17} height={17} />
            </span>
            <div>
              <strong>SLA-Steuerung mit Ampel</strong>
              Kritikalitätsstufen, Reaktions- und Erledigungsfristen, automatische Eskalation.
            </div>
          </li>
          <li>
            <span className="feature-icon">
              <IconRoute width={17} height={17} />
            </span>
            <div>
              <strong>Tourenplanung mit Routenoptimierung</strong>
              Offene Aufträge zu einer Tagestour bündeln – kürzeste Reihenfolge inklusive Ankunftsprognose.
            </div>
          </li>
          <li>
            <span className="feature-icon">
              <IconShield width={17} height={17} />
            </span>
            <div>
              <strong>Granulare Rechteverwaltung</strong>
              Rollen, Gruppen, Einzelrechte und objektbezogene Datensicht – mandantenfähig.
            </div>
          </li>
        </ul>
      </aside>

      <main className="login-main">
        <div className="login-box">
          <div className="stack" style={{ marginBottom: 20, gap: 18 }}>
            <BrandLockup height={54} />
            <div className="row between">
              <div>
                <strong style={{ fontSize: 16 }}>Anmeldung</strong>
                <div className="small subtle">Bitte melden Sie sich mit Ihrem Konto an.</div>
              </div>
              <button className="btn ghost icon-only" onClick={toggle} title="Design umschalten">
                {resolved === 'dark' ? <IconSun /> : <IconMoon />}
              </button>
            </div>
          </div>

          <form className="stack" onSubmit={submit}>
            <ErrorMessage error={error} />

            {/* `id`/`name` zusätzlich zu `autoComplete`: Passwortverwaltungen
                erkennen die Felder daran zuverlässiger, und ohne sie meldet
                der Browser sie als möglicherweise nicht ausfüllbar. Gerade
                hier zählt das – die Anmeldung ist die Seite, auf der Autofill
                gebraucht wird. */}
            <Field label="E-Mail-Adresse" required>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                autoComplete="username"
                autoFocus
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@unternehmen.de"
                required
              />
            </Field>

            <Field label="Passwort" required>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>

            {needsTotp && (
              <Field label="Bestätigungscode (2FA)" hint="6-stelliger Code aus Ihrer Authenticator-App." required>
                <input
                  type="text"
                  id="totp"
                  name="totp"
                  inputMode="numeric"
                  maxLength={6}
                  value={totp}
                  // Einmalcode: Die Verwaltung soll ihn nicht speichern, aber
                  // aus einer SMS oder App übernehmen dürfen.
                  autoComplete="one-time-code"
                  onChange={(event) => setTotp(event.target.value)}
                  autoFocus
                />
              </Field>
            )}

            <button className="btn primary" type="submit" disabled={busy} style={{ width: '100%', padding: 10 }}>
              {busy ? 'Anmeldung läuft …' : 'Anmelden'}
            </button>

            <div className="small subtle" style={{ textAlign: 'center' }}>
              <Link to="/reset">Passwort vergessen?</Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
